import type { AxiosError } from 'axios';
import { ApiResponse } from '../types';
import type { ApiErrorCode } from '../types/api';

/**
 * Error produced by the response interceptor when the backend returns an
 * `ApiResponse` failure envelope carrying a machine-readable `errorCode`.
 * Consumers (e.g. `getFriendlyErrorMessage`) dispatch on `errorCode` and may
 * use `errorContext` for structured details (traceId, currentStatus, ...).
 */
export interface NormalizedApiError extends Error {
  errorCode: ApiErrorCode;
  errorContext?: Record<string, unknown> | null;
  /** User-facing validation bullets from the envelope. Empty if the backend returned none. */
  errors: string[];
  httpStatus: number;
}

/** ASP.NET Core `ValidationProblemDetails` (auto-emitted by `[ApiController]` on model-binding 400s). */
interface ValidationProblemDetails {
  title?: string;
  status?: number;
  errors?: Record<string, string[]>;
}

/**
 * An AxiosError enriched in place with the validation text pulled out of a
 * `ValidationProblemDetails` body. Still a genuine AxiosError — `isAxiosError`,
 * `.response.status` and `.config` all survive — so existing consumers that
 * branch on status keep working; they just get a usable `.message`.
 */
export interface ValidationEnrichedError extends AxiosError {
  /** One bullet per field issue, in backend order. */
  errors: string[];
}

/**
 * True for a ProblemDetails-style field map (`errors` is an OBJECT of arrays).
 * Deliberately excludes `ApiResponse.errors`, which is a flat string ARRAY —
 * those envelopes are already handled by the `errorCode` branch or by callers.
 */
const isValidationProblem = (body: unknown): body is ValidationProblemDetails => {
  if (typeof body !== 'object' || body === null || !('errors' in body)) return false;
  const errors = (body as { errors: unknown }).errors;
  return typeof errors === 'object' && errors !== null && !Array.isArray(errors);
};

/**
 * True for an `ApiResponse` FAILURE envelope that carries a human-readable `message` but no
 * `errorCode` — i.e. a controller's own `ApiResponse.CreateFailure(ex.Message)` path, which is
 * how most hand-written 400s (bad upload, unknown source, business-rule refusal) reach the client.
 */
const isFailureEnvelope = (body: unknown): body is { message: string; errors?: unknown } => {
  if (typeof body !== 'object' || body === null) return false;
  const envelope = body as { success?: unknown; message?: unknown };
  return (
    envelope.success === false &&
    typeof envelope.message === 'string' &&
    envelope.message.trim() !== ''
  );
};

const normalizeWords = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Flattens a ProblemDetails field map into user-readable bullets.
 *
 * The binder path is kept as a locator ("Farms[0].CropGroups[1].CropText: Crop is required")
 * because on nested payloads it's the only way to tell WHICH row failed. It's dropped when the
 * message already names its own field — DataAnnotations messages usually do, and
 * "PlotName: Plot name must be between 2 and 255 characters" reads as a stutter.
 */
const problemDetailsMessages = (problem: ValidationProblemDetails): string[] => {
  const messages: string[] = [];
  for (const [field, fieldMessages] of Object.entries(problem.errors ?? {})) {
    // Keys arrive as "PlotName" or "$.farmers[0].name" — strip the JSON-pointer noise prefix.
    const cleanField = field.replace(/^\$\.?/, '');
    const leaf = cleanField.split('.').pop()?.replace(/\[\d+\]/g, '') ?? '';
    for (const message of fieldMessages) {
      const selfDescribing = leaf !== '' && normalizeWords(message).startsWith(normalizeWords(leaf));
      messages.push(cleanField && !selfDescribing ? `${cleanField}: ${message}` : message);
    }
  }
  if (messages.length === 0 && problem.title) messages.push(problem.title);
  return messages;
};

/**
 * Pure error-handler for the response interceptor's non-auth path.
 *
 * Lives in its own module so tests can exercise envelope normalization and
 * the legacy 429/409 fallback directly — `services/api.ts` is commonly
 * replaced via `mock.module('../api', ...)` by sibling service tests, which
 * would otherwise shadow any handler exported from there.
 *
 * Order (matches interceptor contract):
 *   1. Envelope normalization if `error.response.data.errorCode` is present
 *      (any status). Runs BEFORE the 429/409 short-circuit so envelope-bearing
 *      conflicts/rate-limits retain their structured errorCode.
 *   2. `ValidationProblemDetails` enrichment — axios sets `.message` to the
 *      useless "Request failed with status code 400", so the field messages are
 *      lifted onto it. The error stays an AxiosError (see ValidationEnrichedError).
 *   3. Legacy 429/409 bare-Error fallback for envelope-less responses.
 *   4. `ApiResponse` failure envelope without an errorCode — its `message` (the controller's own
 *      reason) is lifted onto the AxiosError, same enrich-in-place contract as (2).
 *   5. Default: reject with the raw axios error.
 *
 * The 401 refresh branch lives in the interceptor itself (stateful; needs
 * access to the in-flight refresh queue) and is NOT delegated here.
 */
export const handleNonAuthError = (error: AxiosError): Promise<never> => {
  const envelope = error.response?.data as Partial<ApiResponse<unknown>> | undefined;
  if (envelope?.errorCode) {
    const normalized: NormalizedApiError = Object.assign(
      new Error(envelope.message || 'Request failed'),
      {
        errorCode: envelope.errorCode,
        errorContext: envelope.errorContext ?? null,
        errors: envelope.errors ?? [],
        httpStatus: error.response?.status ?? 0,
      },
    );
    return Promise.reject(normalized);
  }

  // `[ApiController]` short-circuits model validation BEFORE the action runs, so these 400s never
  // pass through the controllers' own `ApiResponse` error path — they arrive as a bare ProblemDetails
  // field map with no `message`. Without this the user only ever saw the caller's generic fallback.
  const body = error.response?.data;
  if (isValidationProblem(body)) {
    const messages = problemDetailsMessages(body);
    if (messages.length > 0) {
      const enriched = error as ValidationEnrichedError;
      enriched.message = messages.join(' ');
      enriched.errors = messages;
      return Promise.reject(enriched);
    }
  }

  if (error.response?.status === 429 || error.response?.status === 409) {
    const message = (error.response.data as { message?: string } | null | undefined)?.message
      || 'A calculation is already in progress. Please wait.';
    return Promise.reject(new Error(message));
  }

  // A controller's own failure envelope (`ApiResponse.CreateFailure`) states the real reason in
  // `message` but carries no `errorCode`, so it used to fall straight through to the raw axios
  // error — leaving `.message` as "Request failed with status code 400" and every caller showing
  // its generic `catch` fallback ("Failed to import shapefile") instead of "…contains no .shp file".
  // Enriched in place, like the ProblemDetails branch: consumers that branch on `.response.status`
  // or `isAxiosError` keep working.
  if (isFailureEnvelope(body)) {
    const enriched = error as ValidationEnrichedError;
    enriched.errors = Array.isArray(body.errors)
      ? body.errors.filter((entry): entry is string => typeof entry === 'string')
      : [];
    // Bullets are appended only when they add detail — many envelopes repeat the message verbatim.
    const extra = enriched.errors.filter((entry) => entry !== body.message);
    enriched.message = extra.length > 0 ? `${body.message} ${extra.join(' ')}` : body.message;
    return Promise.reject(enriched);
  }

  return Promise.reject(error);
};
